import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  Divider,
  Chip,
  Stack,
  FormControl,
  CircularProgress,
  Button,
  Alert,
  Avatar,
  Grid
} from '@mui/material';
import SearchIcon from "@mui/icons-material/Search";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import TrophyIcon from '@mui/icons-material/EmojiEvents';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppState } from '../store';
import "../pages/Tools.css";
import { motion } from "framer-motion";
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech'; // For winner badge

interface Match {
  round: number;
  player1: { user: string; asset_ids: string[]; };
  player2: { user: string; asset_ids: string[]; };
  winner: { user: string; };
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: "white",
  fontWeight: "bold",
  borderBottom: "1px solid rgba(255,255,255,0.1)",
  padding: "12px 16px",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    transition: 'background-color 0.3s',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const Player = ({ player, isWinner }: { player: any, isWinner: boolean }) => {
  if (!player || !player.user) {
    return (
      <Box sx={{ p: 1.5, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="rgba(255,255,255,0.5)">Waiting for player...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 1.5,
        borderRadius: '8px',
        background: isWinner ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
        border: isWinner ? '1px solid gold' : '1px solid transparent',
        position: 'relative',
        width: '100%',
        justifyContent: 'center',
        textAlign: 'center',
        transition: 'background 0.3s, border 0.3s'
      }}
    >
      {isWinner && (
        <MilitaryTechIcon
          sx={{
            color: 'gold',
            position: 'absolute',
            top: 4,
            right: 4,
            fontSize: '1.5rem',
          }}
        />
      )}
      <Avatar sx={{ width: 40, height: 40, mb: 1, bgcolor: '#5c3c10' }}>
        {player.user.substring(0, 2)}
      </Avatar>
      <Typography variant="body1" fontWeight="bold" color={isWinner ? 'gold' : 'white'} noWrap>
        {player.user}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center', mt: 1 }}>
        {(player.asset_ids || []).map((id: string) => (
          <Chip
            key={id}
            label={`#${id}`}
            size="small"
            sx={{
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              fontSize: '0.65rem'
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

const Match = ({ match }: { match: Match }) => {
  const { player1, player2, winner, round } = match;
  const player1isWinner = winner.user === player1.user;
  const player2isWinner = winner.user === player2.user;

  // A player is only a "loser" if a winner has been declared
  const player1isLoser = !player1isWinner && !!winner.user;
  const player2isLoser = !player2isWinner && !!winner.user;

  const PlayerDisplay = ({ player, isWinner, isLoser }: { player: any; isWinner: boolean; isLoser: boolean; }) => (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      opacity: isLoser ? 0.6 : 1,
      transition: 'opacity 0.3s ease-in-out',
      flex: 1
    }}>
      <Avatar sx={{ width: 36, height: 36, bgcolor: isWinner ? 'rgba(255, 215, 0, 0.2)' : 'rgba(0,0,0,0.3)', border: isWinner ? '1px solid gold' : '1px solid transparent' }}>
        {player.user ? player.user.substring(0, 2) : '?'}
      </Avatar>
      <Box sx={{ overflow: 'hidden' }}>
        <Typography variant="body2" fontWeight="bold" color={isWinner ? 'gold' : 'white'} noWrap>
          {player.user || 'N/A'}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: 0.5 }}>
          {(player.asset_ids || []).map((id: string) => (
            <Chip key={id} label={`#${id}`} size="small" sx={{ height: '18px', fontSize: '0.65rem', bgcolor: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.8)', borderRadius: '4px' }} />
          ))}
        </Box>
      </Box>
      {isWinner && <MilitaryTechIcon sx={{ color: 'gold', ml: 'auto', alignSelf: 'center' }} />}
    </Box>
  );

  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ scale: 1.03, zIndex: 1, transition: { duration: 0.2 } }}
      style={{ position: 'relative' }}
    >
      <Paper sx={{
        p: 1.5,
        position: 'relative',
        background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02))',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px'
      }}>
        <Chip label={`R${round}`} size="small" sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#ffcc33', color: '#000', fontWeight: 'bold', fontSize: '0.7rem' }} />
        <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }}>
          <PlayerDisplay player={player1} isWinner={player1isWinner} isLoser={player1isLoser} />
          <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>VS</Typography>
          </Divider>
          <PlayerDisplay player={player2} isWinner={player2isWinner} isLoser={player2isLoser} />
        </Stack>
      </Paper>
    </motion.div>
  );
}

export const TournamentResultView = ({ id }: any) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { gameStatus } = useSelector((state: AppState) => state.userModule);
  const [searchQuery, setSearchQuery] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [filteredRows, setFilteredRows] = useState<Array<any>>([]);
  const [rows, setRows] = useState<Array<any>>([]);
  const [tournamentInfo, setTournamentInfo] = useState<any>(null);
  const [winnerReward, setWinnerReward] = useState<any>(0);
  const [runnerUpReward, setRunnerUpReward] = useState<any>(0);

  const tournamentInfoMemo = useMemo(() => {
    const tournamentId = Math.abs(id);
    return gameStatus.tournaments.find((t: any) => t.id === tournamentId) || null;
  }, [id, gameStatus.tournaments]);

  const { formattedMatches } = useMemo(() => {
    if (!tournamentInfoMemo) return { winnerReward: 0, runnerUpReward: 0, formattedMatches: [] };

    const entries = tournamentInfoMemo.entries || [];
    const amount = parseFloat(tournamentInfoMemo.total_fees);
    const symbol = (tournamentInfoMemo.total_fees || '').split(' ')[1] || '';

    let winnerReward: string | number = 0;
    let runnerUpReward: string | number = 0;

    if (entries.length > 1) {
      winnerReward = `${(amount / 2)} ${symbol}`;
      runnerUpReward = `${(amount / 4)} ${symbol}`;
    } else {
      winnerReward = `${amount} ${symbol}`;
    }

    const findEntry = (user: string) => entries.find((e: any) => e.player === user);

    const formattedMatches = (tournamentInfoMemo.matches || []).map((match: any) => {
      const player1User = match.players?.[0]?.player1 || "N/A";
      const player2User = match.players?.[0]?.player2 || "N/A";
      const winnerUser = match.players?.[0]?.winner || "";

      const player1Entry = findEntry(player1User);
      const player2Entry = findEntry(player2User);

      return {
        round: match.round,
        player1: { user: player1User, asset_ids: player1Entry?.asset_ids || [] },
        player2: { user: player2User, asset_ids: player2Entry?.asset_ids || [] },
        winner: { user: winnerUser },
      };
    });

    return { winnerReward, runnerUpReward, formattedMatches };
  }, [tournamentInfoMemo]);

  const filteredMatches = useMemo(() => {
    if (!searchQuery) return formattedMatches;
    return formattedMatches.filter((match: any) =>
      match.player1.user.toLowerCase().includes(searchQuery.toLowerCase()) ||
      match.player2.user.toLowerCase().includes(searchQuery.toLowerCase()) ||
      match.winner.user.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [formattedMatches, searchQuery]);

  useEffect(() => {
    const foundTournament = gameStatus.tournaments.find((item: any) => item.id === -id);
    if (foundTournament) {
      setTournamentInfo(foundTournament);
      if (Array.isArray(foundTournament.matches)) {
        const entries = foundTournament.entries || [];
        const amount = parseFloat(foundTournament.total_fees);
        const symbol = (foundTournament.total_fees || '').split(' ')[1] || '';

        if (entries.length <= 1) {
          setWinnerReward(amount + ' ' + symbol);
          setRunnerUpReward(0);
        }
        else {
          setWinnerReward((amount / 2) + ' ' + symbol);
          setRunnerUpReward((amount / 4) + ' ' + symbol);
        }

        const findEntry = (user: string) =>
          entries.find((e: any) => e.player === user);

        const formattedRows = foundTournament.matches.map((match: any) => {
          const player1User = match.players?.[0]?.player1 || "";
          const player2User = match.players?.[0]?.player2 || "N/A";
          const winnerUser = match.players?.[0]?.winner || "";

          const player1Entry = findEntry(player1User);
          const player2Entry = findEntry(player2User);
          return {
            round: match.round,
            player1: {
              user: player1User,
              asset_ids: player1Entry?.asset_ids || []
            },
            player2: {
              user: player2User,
              asset_ids: player2Entry?.asset_ids || []
            },
            winner: {
              user: winnerUser
            }
          };
        });

        setRows(formattedRows);
        setFilteredRows(formattedRows);
      }
    } else {
      const fallbackTournament = gameStatus.tournaments.find((item: any) => item.id === Math.abs(id));
      if (fallbackTournament) {
        setTournamentInfo(fallbackTournament);
        if (Array.isArray(fallbackTournament.matches)) {
          const entries = fallbackTournament.entries || [];
          const amount = parseFloat(fallbackTournament.total_fees);
          const symbol = (fallbackTournament.total_fees || '').split(' ')[1] || '';

          if (entries.length <= 1) {
            setWinnerReward(amount + ' ' + symbol);
            setRunnerUpReward(0);
          }
          else {
            setWinnerReward((amount / 2) + ' ' + symbol);
            setRunnerUpReward((amount / 4) + ' ' + symbol);
          }

          const findEntry = (user: string) =>
            entries.find((e: any) => e.player === user);

          const formattedRows = fallbackTournament.matches.map((match: any) => {
            const player1User = match.players?.[0]?.player1 || "";
            const player2User = match.players?.[0]?.player2 || "N/A";
            const winnerUser = match.players?.[0]?.winner || "";

            const player1Entry = findEntry(player1User);
            const player2Entry = findEntry(player2User);
            return {
              round: match.round,
              player1: {
                user: player1User,
                asset_ids: player1Entry?.asset_ids || []
              },
              player2: {
                user: player2User,
                asset_ids: player2Entry?.asset_ids || []
              },
              winner: {
                user: winnerUser
              }
            };
          });

          setRows(formattedRows);
          setFilteredRows(formattedRows);
        }
      } else {
        setRows([]);
        setFilteredRows([]);
        setTournamentInfo(null);
      }
    }
    setIsLoading(false);
  }, [id, JSON.stringify(gameStatus.tournaments)]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
        <CircularProgress sx={{ color: '#e94c58' }} />
      </Box>
    );
  }

  // Check if tournament data is available
  if (!tournamentInfo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
        <Paper
          className="game-paper"
          sx={{
            p: 4,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #3d4a0b 0%, #2c3d0a 100%)',
            border: '4px solid #e94c58',
            textAlign: 'center',
            maxWidth: 500,
          }}
        >
          <Typography variant="h5" color="white" fontWeight="bold" gutterBottom>
            Tournament #{Math.abs(id)} Not Found
          </Typography>
          <Typography variant="body1" color="rgba(255,255,255,0.8)" sx={{ mb: 2 }}>
            The tournament data could not be loaded. This might be because:
          </Typography>
          <Box sx={{ textAlign: 'left', color: 'rgba(255,255,255,0.7)' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • The tournament hasn't been created yet
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Results haven't been declared yet
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • There was an error loading the data
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => window.history.back()}
            sx={{
              mt: 3,
              backgroundColor: "#2eca6a",
              color: "white",
              "&:hover": { backgroundColor: "#25a65a" },
            }}
          >
            Go Back
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={containerVariants} style={{ width: "100%" }}>
      {/* Header */}
      <motion.div variants={itemVariants}>
        <Paper className="game-paper" sx={{ p: 3, mb: 4, borderRadius: '12px', background: 'linear-gradient(135deg, #3d4a0b 0%, #2c3d0a 100%)', border: '4px solid #2eca6a' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '50%', p: 2, height: 80, width: 80 }}>
              <TrophyIcon sx={{ fontSize: 40, color: '#ffcc33' }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" fontWeight="bold" color="white" sx={{ mb: 1 }}>
                Tournament #{-id} Results
              </Typography>
              <Typography variant="body1" color="rgba(255,255,255,0.8)">
                View the complete match history and winners for this tournament
              </Typography>
            </Box>
            <Chip label="COMPLETED" color="success" sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2, px: 2 }} />
          </Stack>
        </Paper>
      </motion.div>

      {/* Winners */}
      <motion.div variants={itemVariants}>
        <Paper elevation={3} className="wood-panel" sx={{ p: 3, mb: 4, borderRadius: '12px', background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)', border: '4px solid #5c3c10' }}>
          <Typography variant="h6" color="white" fontWeight="bold" gutterBottom>Tournament Champions</Typography>
          <Divider sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.2)' }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Paper sx={{ p: 3, borderRadius: '12px', background: 'rgba(255, 215, 0, 0.15)', border: '2px solid rgba(255, 215, 0, 0.3)', textAlign: 'center', flex: 1 }}>
              <EmojiEventsIcon sx={{ fontSize: 48, color: 'gold', mb: 1 }} />
              <Typography variant="h6" color="white" fontWeight="bold" gutterBottom>Champion</Typography>
              <Typography variant="h5" color="gold" fontWeight="bold">{tournamentInfo?.winner1 || "N/A"}</Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ mt: 2 }}>Prize: {winnerReward}</Typography>
            </Paper>
            {runnerUpReward > 0 && (
              <Paper sx={{ p: 3, borderRadius: '12px', background: 'rgba(192, 192, 192, 0.15)', border: '2px solid rgba(192, 192, 192, 0.3)', textAlign: 'center', flex: 1 }}>
                <EmojiEventsIcon sx={{ fontSize: 40, color: 'silver', mb: 1 }} />
                <Typography variant="h6" color="white" fontWeight="bold" gutterBottom>Runner-up</Typography>
                <Typography variant="h5" color="silver" fontWeight="bold">{tournamentInfo?.winner2 || "N/A"}</Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ mt: 2 }}>Prize: {runnerUpReward}</Typography>
              </Paper>
            )}
          </Stack>
        </Paper>
      </motion.div>

      {/* Match History */}
      <motion.div variants={itemVariants}>
        <Paper elevation={3} className="wood-panel" sx={{ p: 3, borderRadius: '12px', background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)', border: '4px solid #5c3c10' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" color="white" fontWeight="bold">Match History</Typography>
            <TextField
              placeholder="Search by player..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} /></InputAdornment>),
              }}
              sx={{
                width: { xs: '100%', sm: '300px' },
                '& .MuiOutlinedInput-root': { color: 'white', backgroundColor: 'rgba(0,0,0,0.2)', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' }, '&.Mui-focused fieldset': { borderColor: '#2eca6a' } },
              }}
            />
          </Box>
          <Divider sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.2)' }} />

          {filteredMatches.length > 0 ? (
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              <Grid container spacing={1.5}>
                {filteredMatches.map((match: Match, index: number) => (
                  <Grid item xs={12} key={index}>
                    <Match match={match} />
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          ) : (
            <Alert severity="info" sx={{ backgroundColor: 'rgba(41, 182, 246, 0.15)', color: 'white', border: '1px solid rgba(41, 182, 246, 0.3)', '& .MuiAlert-icon': { color: '#29b6f6' } }}>
              No matches found.
            </Alert>
          )}
        </Paper>
      </motion.div>
    </motion.div>
  );
};